// src/pages/TrainingFraming.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/authContext';
import Button from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input, Textarea } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';

const API_BASE = process.env.REACT_APP_API_URL;

export default function TrainingFraming() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [motion, setMotion] = useState('');
  const [loadingMotion, setLoadingMotion] = useState(false);

  // Your frame
  const [goal, setGoal] = useState('');
  const [audience, setAudience] = useState('');
  const [myFrame, setMyFrame] = useState('');
  const [whyItMatters, setWhyItMatters] = useState('');

  // Opponent frame
  const [oppFrame, setOppFrame] = useState('');
  const [oppEvidence, setOppEvidence] = useState('');

  // Reframing tactics
  const [limitations, setLimitations] = useState('');
  const [newFrame, setNewFrame] = useState('');
  const [language, setLanguage] = useState('');
  const [metaphor, setMetaphor] = useState('');
  const [story, setStory] = useState('');
  const [values, setValues] = useState('');

  // Submit + feedback
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState(null);

  // Example
  const [exampleLoading, setExampleLoading] = useState(false);
  const [example, setExample] = useState(null);

  useEffect(() => {
    if (!user) navigate('/login');
  }, [user, navigate]);

  // Motion
  const newMotion = async () => {
    setError('');
    setFeedback(null);
    setExample(null);

    setGoal(''); setAudience('');
    setMyFrame(''); setWhyItMatters('');
    setOppFrame(''); setOppEvidence('');
    setLimitations(''); setNewFrame('');
    setLanguage(''); setMetaphor(''); setStory(''); setValues('');

    setLoadingMotion(true);
    try {
      const res = await fetch(`${API_BASE}/api/generate-motion`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate motion');
      setMotion(data.motion);
    } catch (e) {
      console.error(e);
      setError('Could not generate a motion.');
    } finally {
      setLoadingMotion(false);
    }
  };

  const submit = async () => {
    setError('');
    setFeedback(null);

    if (!motion) {
      setError('Generate a motion first.');
      return;
    }
    if (!myFrame.trim()) {
      setError('Add your frame statement.');
      return;
    }
    if (!oppFrame.trim()) {
      setError('Diagnose the opponent’s frame.');
      return;
    }
    if (!newFrame.trim()) {
      setError('Provide the frame you will shift to.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        motion,
        goal: goal.trim(),
        audience: audience.trim(),
        my_frame: myFrame.trim(),
        why_it_matters: whyItMatters.trim(),
        opponent_frame: oppFrame.trim(),
        opponent_evidence: oppEvidence.trim(),
        limitations: limitations.trim(),
        new_frame: newFrame.trim(),
        language: language.trim(),
        metaphor: metaphor.trim(),
        story: story.trim(),
        values: values.trim(),
      };

      const res = await fetch(`${API_BASE}/api/framing-feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to evaluate framing');
      setFeedback(data);
    } catch (e) {
      console.error(e);
      setError(e.message || 'Failed to evaluate framing');
    } finally {
      setSubmitting(false);
    }
  };

  const getExample = async () => {
    if (!motion) {
      setError('Generate a motion first.');
      return;
    }
    setExampleLoading(true);
    setExample(null);
    try {
      const res = await fetch(`${API_BASE}/api/framing-example`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          motion,
          side: 'Unspecified',
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to generate example');
      setExample(data);
    } catch (e) {
      console.error(e);
      setError('Failed to generate example.');
    } finally {
      setExampleLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Framing Training</h1>
          <p className="text-sm text-gray-600">
            Set the frame, diagnose the opponent’s, then expose limitations and shift to your frame using language, metaphor, story, and values.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={newMotion} disabled={loadingMotion}>
            {loadingMotion ? <span className="inline-flex items-center gap-2"><Spinner />Generating…</span> : 'New Motion'}
          </Button>
          <Button variant="secondary" onClick={() => navigate('/training')}>Back to Training</Button>
        </div>
      </div>

      {/* Motion */}
      <Card title="Practice Setup" subtitle="Your motion for this drill">
        <label className="block text-xs font-medium text-gray-600 mb-1">Motion</label>
        <div className="rounded-lg border bg-gray-50 px-3 py-2 text-sm text-gray-800 min-h-[40px]">
          {motion || 'Click "New Motion" to begin.'}
        </div>
      </Card>

      {/* Your frame */}
      <Card title="Set Your Frame" subtitle="Clarify what outcome you want, who you are persuading, and your one-line frame">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Goal</label>
            <Input placeholder="What outcome are you trying to achieve?" value={goal} onChange={(e) => setGoal(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Audience</label>
            <Input placeholder="Who are you persuading?" value={audience} onChange={(e) => setAudience(e.target.value)} />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-600 mb-1">Your Frame (one line)</label>
          <Input placeholder='e.g., “This debate is about safeguarding opportunity for working families.”' value={myFrame} onChange={(e) => setMyFrame(e.target.value)} />
        </div>
        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-600 mb-1">Why It Matters</label>
          <Textarea rows={2} placeholder="Impact-level justification in 1–2 sentences." value={whyItMatters} onChange={(e) => setWhyItMatters(e.target.value)} />
        </div>
      </Card>

      {/* Opponent frame */}
      <Card title="Diagnose Opponent’s Frame" subtitle="State their likely frame and the language that signals it">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Opponent Frame (one line)</label>
          <Input placeholder='e.g., “This debate is about moral duty to help the vulnerable.”' value={oppFrame} onChange={(e) => setOppFrame(e.target.value)} />
        </div>
        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-600 mb-1">Tell-tale Words or Phrases</label>
          <Textarea rows={2} placeholder="List 3–6 terms or phrases that reveal their frame." value={oppEvidence} onChange={(e) => setOppEvidence(e.target.value)} />
        </div>
      </Card>

      {/* Reframe plan */}
      <Card title="Reframe Plan" subtitle="Expose limitations, then introduce your frame and reinforce it with language, metaphor, story, and values">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Expose Limitations</label>
            <Textarea rows={3} placeholder="Point out what their frame leaves out or misweights. 2–4 bullets." value={limitations} onChange={(e) => setLimitations(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">New Frame (one line)</label>
            <Input placeholder='e.g., “This debate is about protecting long-term resilience and fairness.”' value={newFrame} onChange={(e) => setNewFrame(e.target.value)} />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Language Choices</label>
            <Textarea rows={2} placeholder='Short word bank, e.g., “resilience”, “fair share”, “security”, “opportunity”.' value={language} onChange={(e) => setLanguage(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Metaphor</label>
            <Input placeholder='e.g., “A shock absorber for families.”' value={metaphor} onChange={(e) => setMetaphor(e.target.value)} />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Story Snippet</label>
            <Textarea rows={3} placeholder="2–4 lines; human-level anecdote that illustrates your frame." value={story} onChange={(e) => setStory(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Values to Appeal To</label>
            <Textarea rows={3} placeholder="Name the values and show the link, e.g., fairness, security, opportunity." value={values} onChange={(e) => setValues(e.target.value)} />
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button onClick={submit} disabled={submitting}>
            {submitting ? <span className="inline-flex items-center gap-2"><Spinner />Scoring…</span> : 'Submit for Feedback'}
          </Button>
        </div>

        {error && (
          <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </Card>

      {/* Feedback */}
      {feedback && (
        <Card title="AI Feedback" subtitle="Scores weight frame clarity, opponent diagnosis, and reframing strength">
          <div className="text-sm text-gray-800 space-y-3">
            <div className="rounded-lg border border-gray-200 p-4 grid sm:grid-cols-4 gap-2">
              {'scores' in feedback && feedback.scores && (
                <>
                  {'frame_clarity' in feedback.scores && <p><strong>Frame Clarity:</strong> {feedback.scores.frame_clarity}/10</p>}
                  {'audience_alignment' in feedback.scores && <p><strong>Audience Alignment:</strong> {feedback.scores.audience_alignment}/10</p>}
                  {'opponent_frame_accuracy' in feedback.scores && <p><strong>Opponent Frame Accuracy:</strong> {feedback.scores.opponent_frame_accuracy}/10</p>}
                  {'limitation_exposure' in feedback.scores && <p><strong>Limitation Exposure:</strong> {feedback.scores.limitation_exposure}/10</p>}
                  {'reframing_effectiveness' in feedback.scores && <p><strong>Reframing Effectiveness:</strong> {feedback.scores.reframing_effectiveness}/10</p>}
                  {'language_power' in feedback.scores && <p><strong>Language Power:</strong> {feedback.scores.language_power}/10</p>}
                  {'consistency_reinforcement' in feedback.scores && <p><strong>Consistency:</strong> {feedback.scores.consistency_reinforcement}/10</p>}
                  {'overall' in feedback.scores && <p><strong>Overall:</strong> {feedback.scores.overall}/10</p>}
                </>
              )}
            </div>

            {feedback.comments && (
              <div className="rounded-lg border border-gray-200 p-4 whitespace-pre-wrap">{feedback.comments}</div>
            )}

            {Array.isArray(feedback.suggested_lines) && feedback.suggested_lines.length > 0 && (
              <div className="rounded-lg border border-gray-200 p-4">
                <p className="font-semibold mb-2">Suggested Lines</p>
                <ul className="list-disc list-inside space-y-1">
                  {feedback.suggested_lines.map((line, i) => <li key={i} className="whitespace-pre-wrap">{line}</li>)}
                </ul>
              </div>
            )}

            {feedback.tips && (
              <div className="rounded-lg border border-gray-200 p-4 whitespace-pre-wrap">
                <strong>Tips:</strong> {feedback.tips}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Example */}
      <Card title="AI Framing Example" subtitle="Optional: see a compact example after feedback">
        <Button onClick={getExample} disabled={exampleLoading}>
          {exampleLoading ? <span className="inline-flex items-center gap-2"><Spinner />Generating…</span> : 'Generate Example'}
        </Button>

        {example && (
          <div className="mt-4 rounded-lg border border-gray-200 p-4 text-sm text-gray-800 space-y-2">
            <p><strong>Opponent Frame:</strong> {example.opponent_frame}</p>
            <p><strong>Your Frame:</strong> {example.my_frame}</p>
            <p><strong>Limitations to Expose:</strong> {example.limitations}</p>
            <p><strong>New Frame Line:</strong> {example.new_frame}</p>
            <p><strong>Language:</strong> {Array.isArray(example.language) ? example.language.join(', ') : example.language}</p>
            <p><strong>Metaphor:</strong> {example.metaphor}</p>
            <p><strong>Story:</strong> <span className="whitespace-pre-wrap">{example.story}</span></p>
            <p><strong>Values:</strong> {Array.isArray(example.values) ? example.values.join(', ') : example.values}</p>
            {Array.isArray(example.suggested_lines) && example.suggested_lines.length > 0 && (
              <div>
                <strong>Suggested Lines:</strong>
                <ul className="list-disc list-inside">
                  {example.suggested_lines.map((line, i) => <li key={i} className="whitespace-pre-wrap">{line}</li>)}
                </ul>
              </div>
            )}
            {example.notes && <p className="text-gray-700 whitespace-pre-wrap">{example.notes}</p>}
          </div>
        )}
      </Card>
    </div>
  );
}
